import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, Loader2Icon, XIcon } from 'lucide-react';
const STORAGE_KEY = 'hrmd-offer-seen';
export function OfferModal() {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    const show = () => {
      if (sessionStorage.getItem(STORAGE_KEY)) return;
      sessionStorage.setItem(STORAGE_KEY, '1');
      setOpen(true);
    };
    const timer = window.setTimeout(show, 20000);
    const onLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) show();
    };
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (phone.replace(/\D/g, '').length < 10) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('done'), 900);
  };
  return <AnimatePresence data-id="element-246" data-mp="34a64q0alt6wa">
      {open && <motion.div className="fixed inset-0 z-50 grid place-items-center bg-ink/70 p-5" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={() => setOpen(false)} data-id="element-247" data-mp="3494g0t0o0lw3">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="offer-title" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl" data-id="element-248" data-mp="394a9qe0b74uq">
            <button type="button" onClick={() => setOpen(false)} className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/25" aria-label="Close offer" data-id="element-249" data-mp="2oo1ev3nsdugw">
              <XIcon className="h-5 w-5" data-id="element-250" data-mp="33gv8dszw8b4k" />
            </button>
            <div className="bg-brand px-8 pb-7 pt-9 text-white" data-id="element-251" data-mp="38emychgz9n64">
              <p className="font-display text-lg font-bold uppercase text-white/85" data-id="element-252" data-mp="3cg8hulo0ozpa">Before you go</p>
              <h2 id="offer-title" className="mt-1 font-display text-6xl font-extrabold uppercase leading-none" data-id="element-253" data-mp="1e0m469wdhocd">
                $50 off
              </h2>
              <p className="mt-1 font-display text-2xl font-semibold" data-id="element-254" data-mp="1i3s655cgctd1">your first repair</p>
            </div>
            <div className="p-8" data-id="element-255" data-mp="1ak138n8o4ujr">
              {status === 'done' ? <div className="flex items-start gap-3" role="status" data-id="element-256" data-mp="15q56tp81zfqp">
                  <CheckCircle2Icon className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" data-id="element-257" data-mp="10tesb6bwcs7e" />
                  <p className="text-ink" data-id="element-258" data-mp="wrt7myt4f9mn">
                    <strong className="font-display text-xl" data-id="element-259" data-mp="wooxlksz5jo2">Your code is on its way.</strong>
                    <span className="block text-sm text-ink-muted" data-id="element-260" data-mp="1tey69vnvw5q2">Text it to us or mention it when you book.</span>
                  </p>
                </div> : <form onSubmit={submit} noValidate data-id="element-261" data-mp="1kesyhkes35bd">
                  <p className="text-ink-muted" data-id="element-262" data-mp="1fhjbebev35qp">Enter your mobile number and we’ll text your discount code. Valid on any job.</p>
                  <label className="mt-4 block" data-id="element-263" data-mp="928lk71o7w95">
                    <span className="sr-only" data-id="element-264" data-mp="8yulj5ghbz4v">Mobile number</span>
                    <input type="tel" autoComplete="tel" placeholder="(702) 555-0123" value={phone} onChange={e => setPhone(e.target.value)} className="w-full rounded-lg border border-line px-4 py-3 text-ink outline-none transition-colors duration-150 focus:border-brand focus:ring-2 focus:ring-brand/20" data-id="element-265" data-mp="40jaireg7il2" />
                  </label>
                  {status === 'error' && <p className="mt-2 text-sm font-medium text-red-600" role="alert" data-id="element-266" data-mp="2anh0fbs6x4mv">
                      Please enter a 10-digit phone number.
                    </p>}
                  <button type="submit" disabled={status === 'submitting'} className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-ink py-3.5 font-display text-xl font-bold text-white transition-colors duration-150 hover:bg-ink-soft disabled:opacity-80" data-id="element-267" data-mp="21qpt2386u3cz">
                    {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-268" data-mp="mpy32pjctj7i" />}
                    Text Me My $50 Code
                  </button>
                  <button type="button" onClick={() => setOpen(false)} className="mt-3 w-full text-center text-sm text-ink-muted underline-offset-2 hover:underline" data-id="element-269" data-mp="wq8dk2e08ciz">
                    No thanks, I’ll pay full price
                  </button>
                </form>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}